import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { resolveAvatarUrl, handleAvatarError } from "../utils/profilePicUrl";
import { sanitizeHtml } from "../utils/sanitizeHtml";
import AuthorNameLink from "./shared/AuthorNameLink";
import { getApiBaseUrl } from "../config/api";
import { isEmployer, canManagePost } from "../utils/userRole";
import LookingForJobForm from "./LookingForJobForm";
import FitCoverImage from "./shared/FitCoverImage";

const API_URL = getApiBaseUrl();

/**
 * Open-to-work detail page — employers can message or email the seeker.
 */
export default function LookingForJobDetail({ user, posts }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [fetchedPost, setFetchedPost] = useState(null);
  const [loadingPost, setLoadingPost] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const cachedPost = useMemo(() => {
    const list = Array.isArray(posts) ? posts : [];
    return (
      list.find(
        (p) => String(p.id) === String(id) && p.post_type === "looking_for_job"
      ) || null
    );
  }, [posts, id]);

  const post = fetchedPost || cachedPost;

  useEffect(() => {
    if (!id) return undefined;
    let cancelled = false;

    // Always hit the API with viewer_id so profile-view inbox can notify the seeker.
    if (!cachedPost) setLoadingPost(true);
    const qs = new URLSearchParams({ id: String(id) });
    if (user?.id) qs.set('viewer_id', String(user.id));
    axios
      .get(`${API_URL}/get_looking_for_job_post.php?${qs.toString()}`)
      .then((res) => {
        if (cancelled) return;
        if (res.data?.success && res.data.post) {
          setFetchedPost(res.data.post);
        } else if (!cachedPost) {
          setFetchedPost(null);
        }
      })
      .catch(() => {
        if (!cancelled && !cachedPost) setFetchedPost(null);
      })
      .finally(() => {
        if (!cancelled) setLoadingPost(false);
      });
    return () => {
      cancelled = true;
    };
  }, [cachedPost, id, user?.id]);

  if (loadingPost && !post) {
    return (
      <div style={{ maxWidth: 720, margin: "40px auto", padding: 20, textAlign: "center" }}>
        <p>Loading open-to-work post…</p>
      </div>
    );
  }

  if (!post) {
    return (
      <div style={{ maxWidth: 720, margin: "40px auto", padding: 20, textAlign: "center" }}>
        <p>Looking-for-job post not found.</p>
        <button type="button" onClick={() => navigate("/home")}>
          Back to home
        </button>
      </div>
    );
  }

  const html = sanitizeHtml(
    post.looking_for_job_description || post.content || post.text || ""
  );
  const created = post.created_at
    ? new Date(post.created_at).toLocaleString()
    : "";
  const isOwner = user && String(post.user_id) === String(user.id);
  const canManage = canManagePost(user, post);
  const employerViewer = isEmployer(user);
  const skills = Array.isArray(post.looking_for_job_skills)
    ? post.looking_for_job_skills
    : [];
  const portfolioUrl = post.looking_for_job_portfolio_url || "";
  const contactEmail = post.looking_for_job_contact_email || "";

  const btnBase = {
    display: "inline-block",
    marginBottom: 12,
    marginRight: 10,
    padding: "10px 18px",
    borderRadius: 10,
    fontWeight: 650,
    textDecoration: "none",
    border: "none",
    cursor: "pointer",
    fontSize: 14,
  };

  const messageSeeker = () => {
    if (!post.user_id) return;
    window.dispatchEvent(
      new CustomEvent("cool:open-chat", {
        detail: { userId: Number(post.user_id) },
      })
    );
  };

  const handleDelete = async () => {
    if (!user?.id || !post?.id || deleting) return;
    if (!window.confirm("Delete this open-to-work post?")) return;
    setDeleting(true);
    try {
      const formData = new FormData();
      formData.append("post_id", post.id);
      formData.append("user_id", user.id);
      const res = await fetch(`${API_URL}/delete_post.php`, {
        method: "POST",
        body: formData,
      });
      const data = await res.json();
      if (!data.success) {
        alert(data.message || "Failed to delete this post.");
        return;
      }
      navigate("/home");
    } catch (err) {
      alert(err?.message || "Failed to delete this post.");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div
      style={{
        maxWidth: 780,
        margin: "0 auto",
        padding: "24px 16px 48px",
        background: "#fff",
        minHeight: "100vh",
      }}
    >
      <button
        type="button"
        onClick={() => navigate(-1)}
        style={{
          border: "none",
          background: "transparent",
          color: "#1d4ed8",
          cursor: "pointer",
          fontSize: 14,
          marginBottom: 16,
          padding: 0,
        }}
      >
        ← Back
      </button>

      <FitCoverImage src={post.cover_image} maxHeight={480} />

      <div
        style={{
          display: "inline-block",
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: 0.4,
          textTransform: "uppercase",
          color: "#1d4ed8",
          background: "#dbeafe",
          padding: "3px 8px",
          borderRadius: 4,
          marginBottom: 12,
        }}
      >
        Looking for job
      </div>

      <h1
        style={{
          fontSize: 32,
          fontWeight: 800,
          color: "#0f172a",
          margin: "0 0 12px",
          lineHeight: 1.25,
        }}
      >
        {post.title}
      </h1>

      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "8px 16px",
          marginBottom: 16,
          fontSize: 14,
          color: "#475569",
        }}
      >
        {post.looking_for_job_location && <span>{post.looking_for_job_location}</span>}
        {post.looking_for_job_availability && (
          <span>· Available: {post.looking_for_job_availability}</span>
        )}
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          marginBottom: 24,
          paddingBottom: 16,
          borderBottom: "1px solid #e2e8f0",
        }}
      >
        <img
          src={resolveAvatarUrl(post)}
          onError={handleAvatarError}
          alt=""
          style={{ width: 40, height: 40, borderRadius: "50%", objectFit: "cover" }}
        />
        <div>
          <div style={{ fontWeight: 600 }}>
            <AuthorNameLink user={post} style={{ fontWeight: 600 }} />
          </div>
          {created && (
            <div style={{ fontSize: 12, color: "#64748b" }}>{created}</div>
          )}
        </div>
      </div>

      {skills.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <div
            style={{
              fontSize: 12,
              fontWeight: 650,
              color: "#64748b",
              marginBottom: 8,
              textTransform: "uppercase",
              letterSpacing: 0.4,
            }}
          >
            Skills
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {skills.map((skill) => (
              <span
                key={skill}
                style={{
                  fontSize: 13,
                  padding: "5px 10px",
                  borderRadius: 8,
                  background: "#eff6ff",
                  color: "#1e40af",
                  border: "1px solid #bfdbfe",
                }}
              >
                {skill}
              </span>
            ))}
          </div>
        </div>
      )}

      <div style={{ marginBottom: 20 }}>
        {canManage ? (
          <>
            <p style={{ fontSize: 13, color: "#64748b", marginBottom: 12 }}>
              {isOwner
                ? "This is your open-to-work post. Employers can message or email you from here."
                : "You can edit or delete this open-to-work post as an admin."}
            </p>
            <button
              type="button"
              onClick={() => setShowEdit(true)}
              style={{
                ...btnBase,
                background: "#fff",
                color: "#0f172a",
                border: "1px solid #cbd5e1",
              }}
            >
              Edit post
            </button>
            <button
              type="button"
              onClick={handleDelete}
              disabled={deleting}
              style={{
                ...btnBase,
                background: "#fff",
                color: "#b91c1c",
                border: "1px solid #fecaca",
                cursor: deleting ? "default" : "pointer",
                opacity: deleting ? 0.7 : 1,
              }}
            >
              {deleting ? "Deleting…" : "Delete post"}
            </button>
          </>
        ) : null}

        {!isOwner && employerViewer && user && (
          <button
            type="button"
            onClick={messageSeeker}
            style={{
              ...btnBase,
              background: "#1d4ed8",
              color: "#fff",
            }}
          >
            Message candidate
          </button>
        )}

        {!isOwner && contactEmail && (
          <a
            href={`mailto:${encodeURIComponent(contactEmail)}?subject=${encodeURIComponent(
              `Regarding your open-to-work post: ${post.title || ""}`
            )}`}
            style={{
              ...btnBase,
              background: "#fff",
              color: "#1d4ed8",
              border: "1px solid #1d4ed8",
            }}
          >
            Email candidate
          </a>
        )}

        {portfolioUrl && (
          <a
            href={portfolioUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              ...btnBase,
              background: "#fff",
              color: "#0f172a",
              border: "1px solid #cbd5e1",
            }}
          >
            View portfolio
          </a>
        )}

        {!isOwner && user && !employerViewer && (
          <p style={{ fontSize: 13, color: "#64748b", marginBottom: 12 }}>
            Employers can contact this candidate directly from this post.
          </p>
        )}

        {!user && (
          <p style={{ fontSize: 13, color: "#64748b", marginBottom: 12 }}>
            Log in as an employer to message this candidate.
          </p>
        )}
      </div>

      <div
        className="looking-for-job-body"
        style={{ fontSize: 16, lineHeight: 1.7, color: "#334155" }}
        dangerouslySetInnerHTML={{ __html: html }}
      />
      {showEdit ? (
        <LookingForJobForm
          post={post}
          user={user}
          onClose={() => setShowEdit(false)}
          onSuccess={(updated) => {
            setShowEdit(false);
            if (updated) setFetchedPost(updated);
          }}
        />
      ) : null}
    </div>
  );
}
